import type { Action, PublicId } from 'flow/store/engine/types';
import type { ChangelogInputBundle } from '../../types';
import { Operation } from '../../types';
import type { DiffArrayPath, MergeDiffResult } from './types';
import { decodeJsonPointerSegment, extractActionPublicId } from '../../helpers';

export function getIsMergeImpacted(
  mergeDiff: MergeDiffResult,
  publicId: PublicId,
) {
  const { added, modified, removed } = mergeDiff;
  return added.has(publicId) || modified.has(publicId) || removed.has(publicId);
}

/**
 * Returns the Operation (add/modify/remove) that the action with the given
 * publicId underwent in the merge diff, or null
 */
export function getMergeDiffOperation(
  mergeDiff: MergeDiffResult | null,
  publicId: PublicId,
): Operation | null {
  if (!mergeDiff) return null;
  if (mergeDiff.added.has(publicId)) return Operation.Add;
  if (mergeDiff.modified.has(publicId)) return Operation.Modify;
  if (mergeDiff.removed.has(publicId)) return Operation.Remove;
  return null;
}

export function createChangelogFromAction(action: Action | null): ChangelogInputBundle {
  return {
    props: {} as ChangelogInputBundle['props'],
    version: {
      actions: action ? { [action.publicId]: action } : {},
      thresholds: {},
      dataRoutes: {},
    },
  };
}

/**
 * Go through each path to extract only relevant operations. Requires an escape character check
 * plus other stuff to make sure the string path is legible
 */
export function extractActionFieldSegments(path: string): string | null {
  const actionPublicId = extractActionPublicId(path);
  if (!actionPublicId) return null;

  const [, remainder = ''] = path.split(`/${actionPublicId}`);
  return remainder.split('/').filter(Boolean).map(decodeJsonPointerSegment).join('/');
}

/**
 * Maps the operations add, replace, move, and remove to the correct diff-status.
 * Does not support test and copy since those do not translate to diffStatus values.
 */
export function readOp(op: string): Operation | null {
  if (op === 'add') return Operation.Add;
  if (op === 'replace' || op === 'move') return Operation.Modify;
  if (op === 'remove') return Operation.Remove;
  return null;
}

/**
 * Reads the value at a slash-joined field path off an action.
 * `arrayPath` is a path ('tag/outcomes'), not a key, so it has to be walked segment by
 * segment - `action['tag/outcomes']` would just be undefined.
 */
export function getAt(action: Action | null, fieldPath: string): any {
  if (!action || !fieldPath) return undefined;
  return fieldPath.split('/').reduce<any>(
    (node, piece) => (node == null ? undefined : node[piece]),
    action,
  );
}

/**
 * Which property identifies an item inside a given array field, keyed by the array's field path.
 */
const ARRAY_ITEM_IDENTITY: Record<string, string> = {
  'tag/outcomes': 'outcome',
};

/**
 * Resolves the stable identity of an array item (in order to solve to give unique ids to all
 * possible values in a field -- whether they are in an array or not). Returns null when the
 * item is an object whose array has no registered identity property.
 */
export function getItemIdentity(item: unknown, arrayPath: string): string | null {
  // Primitives are their own identity, so arrays like `output/expression` work without config.
  if (item === null || typeof item !== 'object') return String(item);

  const identityProperty = ARRAY_ITEM_IDENTITY[arrayPath];
  if (!identityProperty) return null;

  return String((item as Record<string, unknown>)[identityProperty]);
}

/**
 * Checks if the path has an array in it, and returns the path to that array plus the segment that
 * indexes into it (null when the path terminates on the array itself).
 * Returns null when the path holds no array, or when a segment doesn't resolve.
 * */
export function findArrayAncestor(action: Action, path: string[]): DiffArrayPath | null {
  // node starts as an action, but will change types as we walk down the tree via the path
  let node: any = action;
  const walked: string[] = [];

  for (const piece of path) {
    if (Array.isArray(node)) {
      return { arrayPath: walked.join('/'), indexSeg: piece };
    }
    node = node?.[piece];
    if (node === undefined) return null;
    walked.push(piece);
  }

  return Array.isArray(node) ? { arrayPath: walked.join('/'), indexSeg: null } : null;
}
